"use client";

import clsx from "clsx";
import { Fragment, type ComponentType, type ReactNode } from "react";

import { BlogMdxField } from "@/components/common/RuntimeMdx";
import type { Fields } from "@puckeditor/core";
import {
  clampColumnCount,
  COLUMN_SLOT_KEYS,
  getColumnCellClassName,
  getColumnContainerClassName,
  normalizeColumnBorderStyle,
  normalizeColumnVerticalAlign,
  type ColumnBorderStyle,
  type ColumnCount,
  type ColumnVariant,
  type ColumnVerticalAlign,
} from "@/utils/columnStyles";
import { splitEmphasisSegments } from "@/utils/highlightedText";
import {
  getColumnSectionWrapperClasses,
  normalizeCardRadius,
  normalizeSectionBackground,
  type SectionBackground,
} from "@/utils/cardStyles";
import { hasStudioEditableText } from "@/utils/blogMdxSource";
import type { CardRadius } from "@/puck/types";

export type ColumnsHeaderAlign = "left" | "center";

export type ColumnsBlockBaseProps = {
  columnCount?: ColumnCount;
  showBorder?: ColumnBorderStyle;
  columnVerticalAlign?: ColumnVerticalAlign;
  columnBackground?: SectionBackground;
  columnRadius?: CardRadius;
  column1?: import("@puckeditor/core").Slot;
  column2?: import("@puckeditor/core").Slot;
  column3?: import("@puckeditor/core").Slot;
  column4?: import("@puckeditor/core").Slot;
  column5?: import("@puckeditor/core").Slot;
  column6?: import("@puckeditor/core").Slot;
  /** Legacy markdown columns migrated from MDX imports (blog only). */
  leftBody?: string;
  rightBody?: string;
};

export type ColumnsBlockWithHeaderProps = ColumnsBlockBaseProps & {
  eyebrow?: string;
  /** Wrap highlighted phrases in `<em>` tags to render them in brand blue. */
  title?: string;
  subtitle?: string;
  headerAlign?: ColumnsHeaderAlign;
};

const COLUMN_COUNT_FIELD = {
  type: "select" as const,
  label: "Columns",
  options: [
    { label: "1", value: 1 },
    { label: "2", value: 2 },
    { label: "3", value: 3 },
    { label: "4", value: 4 },
    { label: "5", value: 5 },
    { label: "6", value: 6 },
  ],
};

const BORDER_FIELD = {
  type: "radio" as const,
  label: "Border",
  options: [
    { label: "None", value: "none" },
    { label: "All", value: "all" },
    { label: "Top and bottom", value: "top-bottom" },
  ],
};

const VERTICAL_ALIGN_FIELD = {
  type: "radio" as const,
  label: "Column vertical alignment",
  options: [
    { label: "Top", value: "top" },
    { label: "Center", value: "center" },
  ],
};

const COLUMN_BACKGROUND_FIELD = {
  type: "select" as const,
  label: "Section background",
  options: [
    { label: "None", value: "none" },
    { label: "Voice card", value: "voiceCard" },
    { label: "Soft blue", value: "softBlue" },
    { label: "White", value: "white" },
    { label: "Surface", value: "surface" },
  ],
};

const COLUMN_RADIUS_FIELD = {
  type: "select" as const,
  label: "Section corner radius",
  options: [
    { label: "XL", value: "xl" },
    { label: "2XL", value: "2xl" },
    { label: "3XL", value: "3xl" },
  ],
};

function createColumnSlotFields(selfBlockName: string) {
  return Object.fromEntries(
    COLUMN_SLOT_KEYS.map((key, index) => [
      key,
      {
        type: "slot" as const,
        label: `Column ${index + 1}`,
        disallow: [selfBlockName],
      },
    ]),
  );
}

const EMPTY_COLUMN_DEFAULTS = Object.fromEntries(
  COLUMN_SLOT_KEYS.map((key) => [key, []]),
);

function ColumnSide({
  children,
  index,
  borderStyle,
  variant,
  hasSectionBackground,
}: Readonly<{
  children: ReactNode;
  index: number;
  borderStyle: ColumnBorderStyle;
  variant: ColumnVariant;
  hasSectionBackground?: boolean;
}>) {
  return (
    <div
      className={getColumnCellClassName(index, borderStyle, variant, {
        hasSectionBackground,
      })}
    >
      {children}
    </div>
  );
}

function isLegacyTwoColumn(props: {
  leftBody?: string;
  rightBody?: string;
}): boolean {
  return props.leftBody !== undefined || props.rightBody !== undefined;
}

function renderLegacyTwoColumn(
  leftBody: string | undefined,
  rightBody: string | undefined,
  borderStyle: ColumnBorderStyle,
) {
  return (
    <div className={getColumnContainerClassName(2, borderStyle, "blog")}>
      <ColumnSide index={0} borderStyle={borderStyle} variant="blog">
        <BlogMdxField value={leftBody ?? ""} />
      </ColumnSide>
      <ColumnSide index={1} borderStyle={borderStyle} variant="blog">
        <BlogMdxField value={rightBody ?? ""} />
      </ColumnSide>
    </div>
  );
}

const EmptyColumnSlot: ComponentType<{ className?: string }> = () => null;

function renderColumnSlots(
  columnCount: ColumnCount,
  slots: Array<ComponentType<{ className?: string }> | undefined>,
  borderStyle: ColumnBorderStyle,
  variant: ColumnVariant,
  slotClassName: string,
  columnVerticalAlign: ColumnVerticalAlign,
  hasSectionBackground: boolean,
) {
  return (
    <div
      className={getColumnContainerClassName(
        columnCount,
        borderStyle,
        variant,
        columnVerticalAlign,
      )}
    >
      {slots.slice(0, columnCount).map((Column, index) => {
        const ColumnSlot = Column ?? EmptyColumnSlot;
        return (
          <ColumnSide
            key={COLUMN_SLOT_KEYS[index]}
            index={index}
            borderStyle={borderStyle}
            variant={variant}
            hasSectionBackground={hasSectionBackground}
          >
            <ColumnSlot className={slotClassName} />
          </ColumnSide>
        );
      })}
    </div>
  );
}

function renderEmphasisTitle(title: string) {
  return splitEmphasisSegments(title).map((segment, index) =>
    segment.emphasized ? (
      <span key={`${segment.text}-${index}`} className="text-gail-primary">
        {segment.text}
      </span>
    ) : (
      <Fragment key={`${segment.text}-${index}`}>{segment.text}</Fragment>
    ),
  );
}

function renderHeaderText(content: string | ReactNode): ReactNode {
  if (typeof content !== "string") return content;
  return renderEmphasisTitle(content);
}

function ColumnsSectionHeader({
  eyebrow,
  title,
  subtitle,
  headerAlign = "left",
}: Readonly<{
  eyebrow?: string | ReactNode;
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  headerAlign?: ColumnsHeaderAlign;
}>) {
  const isCentered = headerAlign === "center";

  return (
    <>
      {eyebrow ? (
        <p
          className={clsx(
            "font-monument text-sm uppercase tracking-[0.12em] text-gail-primary",
            isCentered && "text-center",
          )}
        >
          {renderHeaderText(eyebrow)}
        </p>
      ) : null}
      {title ? (
        <h2
          className={clsx(
            "mt-3 font-monument text-[2rem] leading-[1.12] tracking-tight text-black md:text-[2.5rem]",
            isCentered ? "mx-auto max-w-3xl text-center" : "max-w-2xl",
          )}
        >
          {renderHeaderText(title)}
        </h2>
      ) : null}
      {subtitle ? (
        <p
          className={clsx(
            "mt-3 font-monument text-base leading-relaxed text-black/60 md:text-lg",
            isCentered ? "mx-auto max-w-2xl text-center" : "max-w-2xl",
          )}
        >
          {renderHeaderText(subtitle)}
        </p>
      ) : null}
    </>
  );
}

export function renderColumnsBlock(
  props: ColumnsBlockWithHeaderProps,
  variant: ColumnVariant,
) {
  const {
    columnCount,
    showBorder,
    columnVerticalAlign,
    columnBackground,
    columnRadius,
    column1,
    column2,
    column3,
    column4,
    column5,
    column6,
    leftBody,
    rightBody,
    eyebrow,
    title,
    subtitle,
    headerAlign,
  } = props;

  const borderStyle = normalizeColumnBorderStyle(showBorder);
  const verticalAlign = normalizeColumnVerticalAlign(columnVerticalAlign);
  const sectionBackground = normalizeSectionBackground(columnBackground);
  const sectionRadius = normalizeCardRadius(columnRadius);
  const hasSectionBackground = sectionBackground !== "none";
  const sectionWrapperClass = getColumnSectionWrapperClasses({
    background: sectionBackground,
    radius: sectionRadius,
  });

  if (variant === "blog" && isLegacyTwoColumn({ leftBody, rightBody })) {
    return renderLegacyTwoColumn(leftBody, rightBody, borderStyle);
  }

  const count = clampColumnCount(Number(columnCount) || 2);
  const slotClassName =
    variant === "blog" ? "flex flex-col gap-2" : "flex h-full flex-col";

  const columnSlots = [
    column1,
    column2,
    column3,
    column4,
    column5,
    column6,
  ] as Array<ComponentType<{ className?: string }> | undefined>;

  const grid = renderColumnSlots(
    count,
    columnSlots,
    borderStyle,
    variant,
    slotClassName,
    verticalAlign,
    hasSectionBackground,
  );

  const wrappedGrid =
    sectionWrapperClass.length > 0 ? (
      <div className={sectionWrapperClass}>{grid}</div>
    ) : (
      grid
    );

  if (variant === "landing") {
    const hasHeader =
      hasStudioEditableText(eyebrow) ||
      hasStudioEditableText(title) ||
      hasStudioEditableText(subtitle);

    return (
      <section className="w-full px-4 pb-16 md:pb-24">
        <div className="mx-auto w-full max-w-6xl">
          {hasHeader ? (
            <ColumnsSectionHeader
              eyebrow={eyebrow}
              title={title}
              subtitle={subtitle}
              headerAlign={headerAlign}
            />
          ) : null}
          <div className={hasHeader ? "mt-10" : undefined}>{wrappedGrid}</div>
        </div>
      </section>
    );
  }

  return wrappedGrid;
}

type CreateColumnsBlockConfigOptions = {
  blockName: string;
  label: string;
  variant: ColumnVariant;
  withHeader?: boolean;
  headerDefaults?: {
    eyebrow?: string;
    title?: string;
    subtitle?: string;
    headerAlign?: ColumnsHeaderAlign;
  };
  defaultColumnCount?: ColumnCount;
};

export function createColumnsBlockConfig({
  blockName,
  label,
  variant,
  withHeader = false,
  headerDefaults,
  defaultColumnCount = 2,
}: CreateColumnsBlockConfigOptions) {
  const baseFields = {
    columnCount: COLUMN_COUNT_FIELD,
    showBorder: BORDER_FIELD,
    ...(variant === "landing"
      ? {
          columnVerticalAlign: VERTICAL_ALIGN_FIELD,
          columnBackground: COLUMN_BACKGROUND_FIELD,
          columnRadius: COLUMN_RADIUS_FIELD,
        }
      : {}),
    ...createColumnSlotFields(blockName),
  };

  const headerFields = withHeader
    ? {
        eyebrow: { type: "text" as const, contentEditable: true },
        title: {
          type: "text" as const,
          label: "Title (wrap highlights in <em> tags)",
          contentEditable: true,
        },
        subtitle: { type: "text" as const, contentEditable: true },
        headerAlign: {
          type: "radio" as const,
          label: "Header alignment",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
          ],
        },
      }
    : {};

  const defaultProps: ColumnsBlockWithHeaderProps = {
    columnCount: defaultColumnCount,
    showBorder: "none",
    columnVerticalAlign: "top",
    columnBackground: "none",
    columnRadius: "2xl",
    ...EMPTY_COLUMN_DEFAULTS,
    ...(withHeader
      ? {
          eyebrow: headerDefaults?.eyebrow ?? "",
          title: headerDefaults?.title ?? "",
          subtitle: headerDefaults?.subtitle ?? "",
          headerAlign: headerDefaults?.headerAlign ?? "left",
        }
      : {}),
  };

  return {
    label,
    fields: {
      ...headerFields,
      ...baseFields,
    } as Fields<ColumnsBlockWithHeaderProps>,
    defaultProps,
    resolveData: ({ props }: { props: ColumnsBlockWithHeaderProps }) => ({
      props: {
        ...props,
        columnCount: clampColumnCount(Number(props.columnCount) || 2),
        showBorder: normalizeColumnBorderStyle(props.showBorder),
        columnVerticalAlign: normalizeColumnVerticalAlign(
          props.columnVerticalAlign,
        ),
        columnBackground: normalizeSectionBackground(props.columnBackground),
        columnRadius: normalizeCardRadius(props.columnRadius),
      },
    }),
    render: (props: ColumnsBlockWithHeaderProps) =>
      renderColumnsBlock(props, variant),
  };
}
